import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sparkles, Trash2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  listTemplateTokens,
  removeTokenAt,
  setTokenFallback,
  type TemplateToken,
} from "@/lib/messageVariables";

interface VariableChipsProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const prettify = (name: string) => name.replace(/_/g, " ");

/**
 * The day-to-day place to manage fallbacks: one chip per token in the message,
 * edited in place so the pipe syntax never has to be typed.
 */
export function VariableChips({ value, onChange, disabled }: VariableChipsProps) {
  const tokens = listTemplateTokens(value);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState("");

  if (tokens.length === 0) {
    return (
      <p className="text-[11px] text-muted-foreground">
        No variables yet — insert one to personalise this message.
      </p>
    );
  }

  const commit = (token: TemplateToken) => {
    onChange(setTokenFallback(value, token, draft));
    setOpenIndex(null);
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {tokens.map((token, i) => {
        const needsFallback = token.isEnrichment && !token.fallback;
        return (
          <Popover
            key={`${token.start}-${token.name}`}
            open={openIndex === i}
            onOpenChange={(o) => {
              if (disabled) return;
              setOpenIndex(o ? i : null);
              if (o) setDraft(token.fallback || "");
            }}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={disabled}
                className={cn(
                  "inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors",
                  disabled && "pointer-events-none opacity-60",
                  token.isEnrichment
                    ? "border-primary/30 bg-primary/10 text-primary hover:bg-primary/20"
                    : "border-success/30 bg-success/10 text-success hover:bg-success/20"
                )}
              >
                {token.isEnrichment ? <Sparkles className="h-3 w-3 shrink-0" /> : null}
                <span className="truncate font-medium">{prettify(token.name)}</span>
                {token.fallback && (
                  <span className="truncate text-muted-foreground">→ {token.fallback}</span>
                )}
                {needsFallback && <span className="text-muted-foreground">no fallback</span>}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 space-y-3 p-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <Label className="text-xs font-medium">{prettify(token.name)}</Label>
                  <p className="font-mono text-[10px] text-muted-foreground">{token.raw}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-muted-foreground hover:text-destructive"
                  title="Remove from message"
                  onClick={() => {
                    onChange(removeTokenAt(value, token));
                    setOpenIndex(null);
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">
                  Fallback text {token.isEnrichment ? "(required)" : "(optional)"}
                </Label>
                <Input
                  autoFocus
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commit(token);
                    }
                  }}
                  placeholder="e.g. your team"
                  className="h-8 text-sm"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" className="flex-1" onClick={() => commit(token)}>
                  Save
                </Button>
                {token.fallback && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-xs"
                    onClick={() => {
                      setDraft("");
                      onChange(setTokenFallback(value, token, ""));
                      setOpenIndex(null);
                    }}
                  >
                    <X className="mr-1 h-3 w-3" /> Clear
                  </Button>
                )}
              </div>
            </PopoverContent>
          </Popover>
        );
      })}
    </div>
  );
}
